import type { PermissionEntry, TenantType } from '@repo/constants'
import type { InferResponseType } from 'hono/client'
import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { store } from '.'
import type { HonoClient } from './api'

const SESSION_TOKEN_KEY = 'contractor_sessionToken'
const TENANT_KEY = 'contractor_tenant'
const TENANTS_KEY = 'contractor_tenants'

// `type` is optional only because it is persisted: a session stored before
// PET-2999 has none until `/session/verify` refreshes it, which `_contractor`'s
// beforeLoad does on every load. Read it as "unknown means treat it as an
// office", never as "not a contractor".
type TenantInfo = { id: string; name: string; slug: string; type?: TenantType }

export const sessionTokenAtom = atomWithStorage<string | null>(SESSION_TOKEN_KEY, null, undefined, {
  getOnInit: true,
})

export const tenantAtom = atomWithStorage<TenantInfo | null>(TENANT_KEY, null, undefined, {
  getOnInit: true,
})

const tenantsAtom = atomWithStorage<TenantInfo[]>(TENANTS_KEY, [], undefined, {
  getOnInit: true,
})

export const isSwitchingTenantAtom = atom(false)
export const setIsSwitchingTenant = (value: boolean) => store.set(isSwitchingTenantAtom, value)

// Terms-of-service status (PET-733). Deliberately not persisted — it is
// refreshed from session/verify on every load so a newly published version
// re-gates at the next session load, never from stale storage.
type SessionVerifyResponse = InferResponseType<
  HonoClient['portal-auth']['contractor']['session']['verify']['$post']
>
export type TermsStatus = SessionVerifyResponse['terms']
export type TermsCurrentVersion = NonNullable<TermsStatus['currentVersion']>

export const termsStatusAtom = atom<TermsStatus | null>(null)
export const setTermsStatus = (value: TermsStatus | null) => store.set(termsStatusAtom, value)

// Not persisted, for the same reason as the terms status.
type SessionUser = SessionVerifyResponse['user']
export const sessionUserAtom = atom<SessionUser | null>(null)
export const setSessionUser = (value: SessionUser | null) => store.set(sessionUserAtom, value)

// Mirrors `/users/me` (`RBAC-13`) for UX gating only; the backend gate is the
// authority. Null until loaded. Not persisted, so a changed role shows at the
// next page load.
export const permissionsAtom = atom<PermissionEntry[] | null>(null)
export const getPermissions = () => store.get(permissionsAtom)
export const setPermissions = (value: PermissionEntry[]) => store.set(permissionsAtom, value)

export const getSessionToken = () => store.get(sessionTokenAtom)
export const setSessionToken = (token: string) => {
  store.set(sessionTokenAtom, token)
  // A new session may carry another role; the layout reloads the grants.
  store.set(permissionsAtom, null)
}

export const getTenant = () => store.get(tenantAtom)
export const setTenant = (t: TenantInfo) => store.set(tenantAtom, t)

export const getTenants = () => store.get(tenantsAtom)
export const setTenants = (t: TenantInfo[]) => store.set(tenantsAtom, t)

export const destroySessionToken = () => {
  store.set(sessionTokenAtom, null)
  store.set(tenantAtom, null)
  store.set(tenantsAtom, [])
  store.set(termsStatusAtom, null)
  store.set(sessionUserAtom, null)
  store.set(permissionsAtom, null)
}
